import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, Inject, NgModule, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { CheckmarkLoaderModule } from '@commons/contact-modal/checkmark-loader/checkmark-loader.component';
import { FormRgpdInfoModule } from '@commons/form-rgpd-info/form-rgpd-info.component';
import { WizValidators } from '@commons/validators';
import { WindowResizeService } from '@core/services/window-resize/window-resize.service';
import { ContactCIJ } from '@models/event/contact';
import { YouthInfoCenterTypeEnum } from '@models/youth-info-center/youth-info-center-type.enum';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CIJWebService } from '@webservices/event-api/cij.webservice';
import { WINDOW } from '@wizbii/angular-utilities';
import { trackEventWithAtInternet } from '@wizbii/tracking';
import { BehaviorSubject, Observable } from 'rxjs';
import { take } from 'rxjs/operators';

export interface YouthInfoCenterModalConfig {
  panelClass: string;
  backdropClass: string;
  minWidth: string;
  autoFocus: boolean;
  disableClose: boolean;
  position: Record<string, string>;
}

interface ContactYouthInfoCenterData {
  isMobile: boolean;
  centerName: string;
  centerMail: string;
  centerType: YouthInfoCenterTypeEnum;
}

@UntilDestroy()
@Component({
  selector: 'app-youth-info-center-contact-modal',
  templateUrl: './contact-modal.component.html',
  styleUrls: ['./contact-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class YouthInfoCenterContactModalComponent implements OnInit, OnDestroy {
  @HostBinding('class.dialog-form') dialogForm = true;

  get ratio$(): Observable<number> {
    return this.windowResizeService.ratio$;
  }

  get firstName(): FormControl {
    return this.form.get('firstName') as FormControl;
  }

  get lastName(): FormControl {
    return this.form.get('lastName') as FormControl;
  }

  get email(): FormControl {
    return this.form.get('email') as FormControl;
  }

  get phone(): FormControl {
    return this.form.get('phone') as FormControl;
  }

  get subject(): FormControl {
    return this.form.get('subject') as FormControl;
  }

  get content(): FormControl {
    return this.form.get('content') as FormControl;
  }

  get rgpdInfo(): string {
    return 'En cliquant sur "Envoyer le message", j\'accepte que mes données personnelles soient transférées au centre de formation pour qu\'il traite ma demande.';
  }

  saved$ = new BehaviorSubject<boolean>(false);
  saving$ = new BehaviorSubject<boolean>(false);
  centerType: YouthInfoCenterTypeEnum;
  centerName: string;
  centerMail: string;
  form!: FormGroup;
  isMobile: boolean;

  trackEvent = trackEventWithAtInternet;

  constructor(
    readonly dialogRef: MatDialogRef<YouthInfoCenterContactModalComponent>,
    @Inject(WINDOW) private readonly window: any,
    @Inject(MAT_DIALOG_DATA) public data: ContactYouthInfoCenterData,
    private readonly windowResizeService: WindowResizeService,
    private readonly fb: FormBuilder,
    private readonly cijWebService: CIJWebService
  ) {
    this.isMobile = data.isMobile;
    this.form = this.buildForm();
    this.centerType = data.centerType;
    this.centerName = data.centerName;
    this.centerMail = data.centerMail;
  }

  ngOnInit(): void {
    if (this.isMobile) {
      this.dialogRef
        .backdropClick()
        .pipe(untilDestroyed(this))
        .subscribe({ next: () => this.closeDialog() });

      this.dialogRef.addPanelClass('wizbii-mobile-panel-open');
    }
  }

  doSubmit(): void {
    const contact: ContactCIJ = {
      contactFirstName: this.firstName.value,
      contactLastName: this.lastName.value,
      contactEmail: this.email.value,
      contactPhoneNumber: this.phone.value,
      cijEmail: this.centerMail,
      subject: this.subject.value,
      content: this.content.value,
      type: this.centerType,
    };

    this.saving$.next(true);

    this.cijWebService
      .contact(contact)
      .pipe(untilDestroyed(this), take(1))
      .subscribe({
        next: () => {
          this.saving$.next(false);
          this.saved$.next(true);
        },
      });
  }

  ngOnDestroy(): void {
    this.saved$.complete();
    this.saving$.complete();
  }

  private buildForm(): FormGroup {
    return this.fb.group({
      firstName: [undefined, [Validators.required, WizValidators.noWhitespaceValidator]],
      lastName: [undefined, [Validators.required, WizValidators.noWhitespaceValidator]],
      email: [undefined, [Validators.required, WizValidators.email]],
      phone: [undefined, WizValidators.phone],
      subject: [undefined, [Validators.required]],
      content: [undefined, [Validators.required]],
    });
  }

  closeDialog(): void {
    if (this.isMobile) {
      this.dialogRef.addPanelClass('wizbii-mobile-panel-close');
      this.window.setTimeout(() => {
        this.dialogRef.close();
      }, 100);
    } else {
      this.dialogRef.close();
    }
  }
}

@NgModule({
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    CheckmarkLoaderModule,
    FormRgpdInfoModule,
  ],
  declarations: [YouthInfoCenterContactModalComponent],
  exports: [YouthInfoCenterContactModalComponent],
})
export class YouthInfoCenterContactModalModule {}
